import { frameAtTick, PF, yawDegrees, type Grenade, type Replay, type ReplayGrenade, type Side, type Stuff } from "@69ly/shared";

/**
 * Which of the library's stuffs each grenade of a replay was: the same grenade and
 * side, thrown from the lineup's spot with its view, and landing near its landing.
 * Nothing is added to the library: this only recognises.
 */

/** A grenade's release: where the thrower's feet were and where they looked. */
export interface Release {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  /** Exact (format 2, the tick it was let go) or read from the frames around it. */
  exact: boolean;
}

export interface GrenadeMatch {
  /** The grenade's index in the replay. */
  index: number;
  grenade: ReplayGrenade;
  /** The stuff kind it can be; null for a decoy. */
  kind: Grenade | null;
  side: Side;
  release: Release | null;
  /** Where it went off (or ended). */
  landing: [number, number, number] | null;
  stuff: Stuff | null;
  /** In game units, when matched. */
  throwOff: number;
  landingOff: number;
  /** Degrees off the lineup's view, the larger of pitch and yaw. */
  angleOff: number;
  /** Landed where the stuff lands (within half the tolerance). */
  onTarget: boolean;
}

const KIND: Record<ReplayGrenade["kind"], Grenade | null> = { smoke: "smoke", flash: "flash", he: "he", molotov: "molotov", decoy: null };

/** How far a throw may stand from the lineup, by how it is thrown. */
const THROW_TOLERANCE: Record<Stuff["technique"]["movement"], number> = { standing: 32, crouching: 32, walking: 96, running: 200 };
/** How far it may land from the lineup's landing: flashes pop in the air, where a click on the map is rough. */
const LANDING_TOLERANCE: Record<Grenade, number> = { smoke: 96, molotov: 96, he: 128, flash: 192 };
/** View: 2° when the release is exact, more when read from 8-bit frame angles. */
const ANGLE_EXACT = 2;
const ANGLE_FRAMES = 3.5;
/** Feet height at the release: a jump throw is up to ~60 units above the spot. */
const HEIGHT_TOLERANCE = 80;

const angleDiff = (a: number, b: number) => {
  const d = Math.abs((((a - b) % 360) + 540) % 360 - 180);
  return d;
};

/** Where the thrower stood and looked when the grenade left: exact from format 2, else the frame just before it was seen flying. */
export function releaseOf(r: Replay, g: ReplayGrenade): Release | null {
  if (g.from) return { x: g.from[0], y: g.from[1], z: g.from[2], pitch: g.from[3], yaw: g.from[4], exact: true };
  if (g.thrower < 0) return null;
  const round = r.rounds.find((x) => g.throwTick >= x.startTick && g.throwTick <= x.postTick);
  if (!round) return null;
  // The projectile is first seen up to a sample after the release, and the throw animation is about 6 ticks.
  const f = frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, g.throwTick - 8);
  const at = f * r.players.length + g.thrower;
  if (!(r.columns.flags[at] & PF.present)) return null;
  const yaw = yawDegrees(r.columns.yaw[at]);
  return { x: r.columns.x[at], y: r.columns.y[at], z: r.columns.z[at], pitch: r.columns.pitch[at], yaw: yaw > 180 ? yaw - 360 : yaw, exact: false };
}

/** The thrower's side when it was thrown. */
function sideOf(r: Replay, g: ReplayGrenade): Side {
  const round = r.rounds.find((x) => g.throwTick >= x.startTick && g.throwTick <= x.postTick) ?? r.rounds[0];
  const team = r.players[g.thrower]?.team ?? 0;
  return team === round.tTeam ? "t" : "ct";
}

export function landingOf(g: ReplayGrenade): [number, number, number] | null {
  if (g.at) return g.at;
  if (g.path.length >= 3) return [g.path[g.path.length - 3], g.path[g.path.length - 2], g.path[g.path.length - 1]];
  return null;
}

/**
 * Every grenade with its best stuff, if any. `dismissed` holds "grenadeIndex:stuffId"
 * pairs the team marked as wrong: that stuff is not offered for that grenade again.
 */
export function matchGrenades(r: Replay, stuffs: Stuff[], dismissed: ReadonlySet<string> = new Set()): GrenadeMatch[] {
  const onMap = stuffs.filter((s) => s.map === r.map);
  return r.grenades.map((g, index) => {
    const kind = KIND[g.kind];
    const side = sideOf(r, g);
    const release = releaseOf(r, g);
    const landing = landingOf(g);
    const none: GrenadeMatch = { index, grenade: g, kind, side, release, landing, stuff: null, throwOff: 0, landingOff: 0, angleOff: 0, onTarget: false };
    if (!kind || !release || !landing) return none;
    let best: { stuff: Stuff; score: number; throwOff: number; landingOff: number; angleOff: number; landTol: number } | null = null;
    for (const s of onMap) {
      if (s.grenade !== kind || s.side !== side || dismissed.has(`${index}:${s.id}`)) continue;
      const throwTol = THROW_TOLERANCE[s.technique.movement] + (s.technique.jump ? 16 : 0);
      const throwOff = Math.hypot(release.x - s.throw.x, release.y - s.throw.y);
      if (throwOff > throwTol || Math.abs(release.z - s.throw.z) > HEIGHT_TOLERANCE) continue;
      const angleTol = release.exact ? ANGLE_EXACT : ANGLE_FRAMES;
      const angleOff = Math.max(angleDiff(release.yaw, s.throw.yaw), Math.abs(release.pitch - s.throw.pitch));
      if (angleOff > angleTol) continue;
      const landTol = LANDING_TOLERANCE[kind];
      const landingOff = Math.hypot(landing[0] - s.landing.x, landing[1] - s.landing.y);
      if (landingOff > landTol) continue;
      if (s.landing.z !== null && Math.abs(landing[2] - s.landing.z) > 200) continue;
      const score = throwOff / throwTol + angleOff / angleTol + landingOff / landTol;
      if (!best || score < best.score) best = { stuff: s, score, throwOff, landingOff, angleOff, landTol };
    }
    if (!best) return none;
    return { ...none, stuff: best.stuff, throwOff: best.throwOff, landingOff: best.landingOff, angleOff: best.angleOff, onTarget: best.landingOff <= best.landTol / 2 };
  });
}

export interface StuffTally {
  stuff: Stuff;
  thrown: number;
  onTarget: number;
  /** By player index. */
  byPlayer: Map<number, { thrown: number; onTarget: number }>;
}

/** Per stuff over the map: how often it was thrown, how often it landed right, by whom. */
export function tallyStuffs(matches: GrenadeMatch[]): StuffTally[] {
  const out = new Map<string, StuffTally>();
  for (const m of matches) {
    if (!m.stuff) continue;
    const t = out.get(m.stuff.id) ?? { stuff: m.stuff, thrown: 0, onTarget: 0, byPlayer: new Map() };
    t.thrown++;
    if (m.onTarget) t.onTarget++;
    const p = t.byPlayer.get(m.grenade.thrower) ?? { thrown: 0, onTarget: 0 };
    p.thrown++;
    if (m.onTarget) p.onTarget++;
    t.byPlayer.set(m.grenade.thrower, p);
    out.set(m.stuff.id, t);
  }
  return [...out.values()].sort((a, b) => b.thrown - a.thrown || a.stuff.title.localeCompare(b.stuff.title));
}
